import { api } from '../../lib/api'

/** 온보딩 완료 로컬 플래그(서버 플래그의 폴백/캐시). 개편 홍보 재노출 위해 v3 으로 버전업. */
export const ONBOARDING_DONE_KEY = 'heinrich.onboarding.done.v3'

export interface DemoBundle {
  site_id: string
  endpoints: Record<string, unknown>
}

export const onboardingApi = {
  /** 데모 사업장 스냅샷(실제 분석 산출물) — 목 주입 전에 호출(URL 에 __demo__ 없음 → 실서버). */
  demoBundle: () => api.get<DemoBundle>('/onboarding/demo-bundle').then((r) => r.data),
  /** 공개 데모 스냅샷 — 비로그인 방문자(랜딩·소개 페이지 임베드)용. 인증 불필요. */
  demoBundlePublic: () => api.get<DemoBundle>('/onboarding/demo-bundle-public').then((r) => r.data),
  status: () => api.get<{ completed: boolean }>('/onboarding/status').then((r) => r.data),
  /** 진단 모달 단계 진입 기록(이탈 지점 계측) — fire-and-forget, 실패 무시. */
  progress: (last_step: number, total_steps: number) =>
    api.post('/onboarding/progress', { last_step, total_steps }).then((r) => r.data),
  /** 완료/건너뛰기 기록. dismiss 방식(skipped)·마지막 본 단계(0-based)·전체 단계 수를 함께 남긴다. */
  complete: (info?: { skipped?: boolean; last_step?: number; total_steps?: number }) =>
    api.post('/onboarding/complete', info ?? {}).then((r) => r.data),
  /** 2분 진단 — 첫 안전일지(TBM)를 무료(쿼터 미차감)로 생성. 온보딩 중 1회만. */
  generateFirst: (siteId: string, workDescription?: string) =>
    api.post<{ tbm_id: string; status: string; reused: boolean }>(
      '/onboarding/generate-first',
      { site_id: siteId, work_description: workDescription },
    ).then((r) => r.data),
  /** 페이지 기능 소개(인트로 카드) 열람 기록 — 사용자 단위 1회 노출(기기 무관). */
  /** choice: 팝업에서 무엇을 눌렀는가('started' | 'later'). 첫 이관 팝업의
   *  "시도조차 안 함" vs "시도했다 포기" 를 가르는 유일한 신호다(2026-08-21). */
  markIntroSeen: (pageKey: IntroPageKey, lastCard: number, completed: boolean,
                  choice?: 'started' | 'later') =>
    api.post<{ ok: boolean; intro_seen: Record<string, IntroSeenEntry> }>(
      '/onboarding/intro-seen',
      { page_key: pageKey, last_card: lastCard, completed, ...(choice ? { choice } : {}) },
    ).then((r) => r.data),
  /** 작업 서술/문서 텍스트 → 작업 목록 추출(LLM). */
  parseTasks: (text: string) =>
    api.post<{ tasks: ParsedTask[] }>('/onboarding/setup/parse-tasks', { text },
      { timeout: 60_000 }).then((r) => r.data.tasks),
  /** 작업 등록(전체) + 기준 서류 4종 무료 생성(선택 작업 1개·쿼터 미차감·계정 1회·멱등). */
  setupGenerate: (siteId: string, tasks: SetupTaskIn[], detail: SetupDetail,
                  generateTaskName?: string | null) =>
    api.post<{ job_id: string; reused: boolean; planned?: unknown[]
               generated_task_id?: string; generated_task_name?: string }>(
      '/onboarding/setup/generate',
      { site_id: siteId, tasks, detail, generate_task_name: generateTaskName ?? undefined },
    ).then((r) => r.data),
  /** 생성 잡 상태 폴링. */
  setupStatus: (jobId: string) =>
    api.get<SetupJobStatus>(`/onboarding/setup/status/${jobId}`).then((r) => r.data),
  /** 최신 온보딩 생성 잡 — 진단 모달이 재방문 후 진행/완료 화면을 복원할 때. */
  setupCurrent: () =>
    api.get<{ job_id: string | null; status: 'running' | 'done' | null; site_id?: string
              created_count?: number; ra_ref_id?: string | null }>(
      '/onboarding/setup/current').then((r) => r.data),
}

export interface ParsedTask {
  name: string
  category: 'routine' | 'nonroutine'
  work_type: string | null
}
export interface SetupTaskIn {
  name: string
  category: 'routine' | 'nonroutine'
  work_type?: string | null
  hazard_hints?: string[]
  /** 085 — 작업별 현장 상세(작업 카드 인라인 입력). 미입력이면 업종 문맥만으로 생성. */
  detail?: SetupDetail | null
}
export interface SetupDetail {
  location?: string | null
  worker_count?: string | null
  supervisor?: string | null
  equipment?: string[]
  conditions?: string[]
  hazards?: string[]
  safeguards?: string[]
  ppe?: string[]
}
export interface SetupJobStep {
  /** kind:task_id — 작업×문서 유니크 키(같은 kind 가 작업 수만큼 반복될 수 있다). */
  key?: string
  kind: string
  task_id?: string | null
  task_name?: string | null
  label: string
  status: 'pending' | 'running' | 'done' | 'error' | 'skipped'
  note?: string | null
  ref_id?: string | null
}
export interface SetupJobStatus {
  job_id: string
  status: 'running' | 'done' | 'error'
  steps: SetupJobStep[]
  created: { kind: string; ref_id: string; title?: string; count?: number
             task_id?: string | null; task_name?: string | null }[]
  errors: Record<string, string>
  site_id: string
}

/** 인트로 팝업이 있는 페이지 키(백엔드 _INTRO_PAGE_KEYS 와 동일 집합 유지). */
export type IntroPageKey = 'obligations' | 'partners' | 'partner_link'
  | 'ra_review' | 'first_import'   // 온보딩 후속 1회 노출(RA 검토 · 첫 이관 팝업)
export interface IntroSeenEntry { at: string; last_card: number; completed: boolean; choice?: string }
